import React, {
  Suspense,
  lazy,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
// import "./App.css";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PageRoutes, {
  APIRoutes,
  LegacyPageRoutes,
  UserRoles,
} from "./utils/constants";
import HttpService from "./hooks/Https-services";
import { getCurrentAuth } from "./hooks/useAxiosPrivate";
import { commonDetailSlice } from "./store/reducers/commonReducer";
import { AUTH_CHANGED_EVENT } from "./v2/hooks/useAuthState";

import PrivateRoutes from "./Common/RouteAccessor/PrivateRoute";
import PublicRoute from "./Common/RouteAccessor/PublicRoute";
import { SubscriptionStatusProvider } from "./Common/Contex/SubscriptionStatusContex";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import UpcomingEvent from "./Pages/UpcomingEvent";
import OnDemand from "./Pages/OnDemand";
import VerifyEmail from "./Pages/EmailVefiy";
import EditProfile from "./Pages/MyAccount/EditProfile";
import TermAndCondition from "./v2/pages/TermsAndConditions";
import PrivacyPolicy from "./v2/pages/PrivacyPolicy";
import NoPageFound from "./Pages/NoPageFound";
import Payment from "./Pages/Payment/Payment";
import ReactGA from "react-ga4";
import { setGaUserId, setGaUserProperties } from "./utils/gaUser";
import useGaSubscriptionProperties from "./hooks/useGaSubscriptionProperties";
import Profile from "./v2/pages/Profile";
import AccountComingSoon from "./v2/pages/AccountComingSoon";
import ConfigData from "./Config/config.json";

import WatchGame from "./Pages/Watch/watch";
import { Helmet } from "react-helmet";
import { webManifestUrl } from "./utils/webManifest";
import RouteTitle from "./Common/Component/RouteTitle";
import LegacyRedirect from "./Common/Component/LegacyRedirect";

import AdminRoutes from "./Common/RouteAccessor/AdminRoutes";
import GiftPage from "./Pages/GiftPage";
import RedeemGift from "./Pages/RedeemGift";
import SecondScreen from "./Pages/SecondScreen";
import MyNahlTv from "./Pages/MyNAHLTV/MyNahlTv";
import PersonalChannelSubscribe from "./Pages/PersonalChannel/PersonalChannelSubscribe";
import ChannelShareAction from "./Pages/PersonalChannel/ChannelShareAction";
import LoadingContext, {
  LoadingContextProvider,
} from "./Common/Contex/LoadingContex";
import { ThemeProviderReact } from "./Common/Contex/ThemeContex";
import SupportPage from "./Pages/SupportPage";
import Highlights from "./Pages/HighlightPlayer";
import BlockingWrapper from "./Common/RouteAccessor/BlockingWrapper";

import Clips from "./Pages/Clips";
import ClipView from "./Pages/Clips/ClipView";
import FilterClips from "./Pages/Clips/FilterClips";
import Team from "./Pages/Team";

// HTPTV2-11: dev-only preview route for the v2 kitchen sink.

/*
 * THE ADMIN TREE, LAZY.
 *
 * Pages/Admin is the console shell and it imports the whole of it: the alert
 * designer, slider settings, the user list, player alerts, ads, media, polls,
 * whizzard, games and rules. Statically imported, all of that plus the MUI it
 * is built from sat in the main chunk, so every fan who ever loaded a game
 * page downloaded the administration console to never open it.
 *
 * Lazy here rather than a manualChunks rule, because the split follows the
 * only thing that actually matters: whether this person is an administrator.
 *
 * AdminRoutes itself stays eager on purpose. It is a role check with no MUI
 * and nothing behind it, and keeping it in the main chunk means somebody
 * without the role is redirected home without first fetching a chunk to be
 * told no.
 *
 * These suspend into the single <Suspense fallback={<RouteFallback />}> that
 * already wraps <Routes>, so there is no new boundary and no new spinner. It
 * does mean admin is now the first v1 area that can suspend, which the
 * comment on that boundary used to say never happened.
 */
const Admin = lazy(() => import("./Pages/Admin"));
const TinyEditor = lazy(() => import("./Pages/Admin/AlertDesign/TinyEditor"));
const CreateSliderForm = lazy(() => import("./Pages/Admin/SliderSettings/CreateSliderForm"));
const UserView = lazy(() => import("./Pages/Admin/UsersList/UserView"));
const CreatePlayerError = lazy(() => import("./Pages/Admin/PlayerAlertList/PlayerAlertListTable/CreatePlayerError"));
const CreateAds = lazy(() => import("./Pages/Admin/Ads/CreateAds"));
const AddMedia = lazy(() => import("./Pages/Admin/Media/AddMedia"));
const CreatePoll = lazy(() => import("./Pages/Admin/Poll/PollListTable/CreatePoll"));
const EditWhizzard = lazy(() => import("./Pages/Admin/Whizzard/CreateWhizzard"));
const GameView = lazy(() => import("./Pages/Admin/Games/GameView"));
const RuleView = lazy(() => import("./Pages/Admin/Rules/RuleView"));

const V2PreviewRoute = import.meta.env.DEV
  ? lazy(() => import("./v2/pages/Preview/route"))
  : null;

// Preview route for the new v2 Events page design.
const V2EventsRoute = import.meta.env.DEV
  ? lazy(() => import("./v2/pages/Events/route"))
  : null;

// V2 Upcoming Events page route.
const V2UpcomingEventRoute = lazy(
  () => import("./v2/pages/Events/upcoming-route"),
);

// V2 On Demand page route.
const V2OnDemandRoute = lazy(() => import("./v2/pages/Events/vod-route"));
// V2 NA 24x7 live TV / program guide page route.
const V2LiveTVRoute = lazy(() => import("./v2/pages/LiveTV/route"));

// V2 Highlights page route.
const V2HighlightsRoute = lazy(() => import("./v2/pages/Highlights/route"));

// V2 single-clip player page route (/short-view/:id).
const V2ShortViewRoute = lazy(
  () => import("./v2/pages/Highlights/short-view-route"),
);

// V2 channel+type drill-down page route (/short-content/:channel/:type_name/:id/:type).
const V2DrillDownRoute = lazy(
  () => import("./v2/pages/Highlights/drilldown-route"),
);

// V2 Lens page route (Favorites/Universe/Studio sidebar shell).
const V2LensUniverseRoute = lazy(
  () => import("./v2/pages/Lens/universe-route"),
);
const V2LensFavoritesRoute = lazy(
  () => import("./v2/pages/Lens/favorites-route"),
);
const V2LensStudioRoute = lazy(() => import("./v2/pages/Lens/studio-route"));

// V2 Notifications (/notification) page route.
const V2NotificationsRoute = lazy(
  () => import("./v2/pages/Notifications/route"),
);

// V2 Plans (Access Center) page route.
const V2PlansRoute = lazy(() => import("./v2/pages/Plans/route"));

// V2 Checkout page route.
const V2CheckoutRoute = lazy(() => import("./v2/pages/Checkout/route"));

// V2 Purchase Success page route (Task 20: Stripe return_url lands here).
const V2PurchaseSuccessRoute = lazy(
  () => import("./v2/pages/PurchaseSuccess/route"),
);

// V2 Account page route.
const V2AccountRoute = lazy(() => import("./v2/pages/Account/route"));
const V2CancelFlowRoute = lazy(
  () => import("./v2/pages/Account/CancelFlow/route"),
);

// V2 Sign In / Create Account page route.
const V2SignInRoute = lazy(() => import("./v2/pages/SignIn/route"));

// V2 Reset Password page route.
const V2ResetPasswordRoute = lazy(
  () => import("./v2/pages/ResetPassword/route"),
);

// V2 Home page route (phase 1: hero only).
const V2HomeRoute = lazy(() => import("./v2/pages/Home/route"));

import { isGiftPlanId } from "./v2/pages/Checkout/route-guards";
import { RouteFallback } from "./v2/components/RouteFallback";
import { isLiveChannelTenant } from "./v2/lib/liveChannels";

/**
 * Chooses v1 `Payment` or the v2 checkout for the shared /payment route,
 * based on the config flag and whether plan_id is a gift sentinel ("0" or
 * "4"). Gift purchases always stay on v1 regardless of the flag. useV2Layout
 * is driven by the same decision so v1 keeps its chrome and v2 renders bare
 * (it supplies its own header via SidebarLayout).
 */
function PaymentRouteElement() {
  const { plan_id = "" } = useParams();
  const useV2 = !isGiftPlanId(decodeURIComponent(plan_id));
  return (
    <PrivateRoutes useV2Layout={useV2}>
      {useV2 ? (
        <V2CheckoutRoute />
      ) : (
        <Payment />
      )}
    </PrivateRoutes>
  );
}

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * HTPTV2-2: v2's AuthCard can't reach Redux or LoadingContext directly (see
 * src/v2/__tests__/boundaries.test.ts), so it only fires AUTH_CHANGED_EVENT
 * on login. This is the v1-side listener that keeps the legacy `commonDetails`
 * Redux slice in sync afterward, mirroring the old Pages/Login getEntries flow.
 */
function AuthChannelSync() {
  const dispatch = useDispatch();
  const { setIsRefresh }: any = useContext(LoadingContext);
  const commonDetails = useSelector(
    (state: any) => state.commonDetails.commonDetails,
  );
  const commonDetailsRef = useRef(commonDetails);
  commonDetailsRef.current = commonDetails;

  useEffect(() => {
    const sync = async (event: Event) => {
      const auth = getCurrentAuth();
      if (!auth?.AccessToken) {
        // Logout: clear the stale commonDetails left over from the previous
        // session instead of leaving it in Redux until a full page reload.
        dispatch(commonDetailSlice.actions.removecommonDetails());
        setIsRefresh((prev: boolean) => !prev);
        return;
      }
      // Profile mutations (name/photo) also fire AUTH_CHANGED_EVENT to keep
      // this slice's FirstName/LastName/ProfileImage in sync, but they don't
      // change channel membership - skip the GetAllChannel refetch and just
      // merge the refreshed auth fields onto the existing commonDetails.
      const profileOnly = (event as CustomEvent<{ profileOnly?: boolean }>)
        .detail?.profileOnly;
      if (profileOnly) {
        dispatch(
          commonDetailSlice.actions.setcommonDetails({
            ...commonDetailsRef.current,
            ...auth,
          }),
        );
        setIsRefresh((prev: boolean) => !prev);
        return;
      }
      const data: any = { ...auth, isChannelCreated: false };
      try {
        const res = await HttpService.get(APIRoutes.GetAllChannel);
        data.isChannelCreated = !(
          res.data.data == null || res.data.data.length == 0
        );
      } catch {}
      // setcommonDetails fully replaces the slice (state.commonDetails =
      // payload), so a preceding removecommonDetails() is a redundant extra
      // dispatch/re-render, not a needed clear-before-write.
      dispatch(commonDetailSlice.actions.setcommonDetails(data));
      setIsRefresh((prev: boolean) => !prev);
    };
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, sync);
  }, [dispatch, setIsRefresh]);

  return null;
}

function App() {
  /*
   * `config` below is only populated by handleConfig() in a useEffect, so it
   * reads as {} for the route's very first render on a hard refresh - a
   * gate keyed off config.name (like ShortView's) briefly excludes that
   * <Route>, and with nothing else matching "/na-24x7" the catch-all "/*"
   * wins for that one paint (a v1 NoPageFound flash before the effect
   * resolves and the real route replaces it). This reads the same tenant
   * key directly (the same source useConfig() itself reads), synchronously,
   * so the gate is correct from the first render. Can't use the useConfig()
   * hook here: it calls useLocation(), which throws above the <Router> this
   * component renders below.
   */
  const syncTenantName = process.env.REACT_APP_ENV;
  const [faviconUrl, setFaviconUrl] = useState<string>("");
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [manifestData, setMenifest] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  // Lazy initializer, not useState({}): config.json is a static import, not
  // an async fetch, so the real tenant config is already available at this
  // point - there's no actual data to wait for. Starting from {} and only
  // populating it in the useEffect below meant every route below (all
  // gated behind config?.xxx checks) matched nothing on the very first
  // render, so React Router fell through to the catch-all "*" route and
  // painted NoPageFound (full chrome, Footer included) for one frame before
  // the real page appeared on the very next render.
  const [config, setConfig] = useState<any>(
    () => (ConfigData as any)[process.env.REACT_APP_ENV as string] ?? {},
  );
  const searchParams = new URLSearchParams(window.location.search);
  const is_team = searchParams.get("is_team");
  const handleConfig = () => {
    const data: any = ConfigData;
    const websiteName: any = process.env.REACT_APP_ENV;
    setFaviconUrl(data[websiteName]?.favicon);
    setConfig(data[websiteName]);
    // Built from config.appIcons when the tenant has them, else its static file.
    setMenifest(
      webManifestUrl(data[websiteName], `/${process.env.REACT_APP_ENV}manifest.json`),
    );
    setTitle(data[websiteName]?.title);
    setLogoUrl(data[websiteName]?.logo);
    const googleAnalyticsId = data[websiteName]?.gTag;
    ReactGA.initialize(googleAnalyticsId);
    // HTPTV-682: if a session already exists (e.g. page reload), attach the
    // canonical UserID to GA4 right after init.
    setGaUserId();
    // HTPTV-683: tenant is independent of auth - set it as a user property on init.
    setGaUserProperties({ tenant: websiteName });
  };
  const hasGeoLocation = ["usah", "dev"].includes(config.name);
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {})
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);
  useEffect(() => {
    handleConfig();
    return () => {
      const existingFaviconLink = document.querySelector('link[rel="icon"]');
      if (existingFaviconLink) {
        document.head.removeChild(existingFaviconLink);
      }
    };
  }, []);

  // HTPTV-683: set GA4 subscription user properties on authenticated load.
  useGaSubscriptionProperties();

  return (
    /*
     * v7_startTransition makes route changes React transitions, which is what
     * stops the blank flash on the first visit to a page.
     *
     * Routes are lazy and every Suspense boundary here falls back to null, so
     * without it React unmounts the page you are on and renders nothing while
     * the next page's chunk downloads. The second visit is instant because the
     * chunk is cached, which is why it only ever blinked once per page.
     *
     * As a transition, React keeps the current page on screen until the next
     * one is ready to replace it, so there is nothing to see in between.
     */
    <Router future={{ v7_startTransition: true }}>
      <ThemeProviderReact>
        <LoadingContextProvider>
          <AuthChannelSync />
          <Helmet>
            <link rel="icon" type="image/x-icon" href={faviconUrl} />
            <link rel="manifest" href={manifestData} />
            <meta name="description" content={title} />
            <meta name="application-name" content={title} />
            <meta name="theme-color" content="#ffffff" />
            {/* The square icon iOS uses for "Add to Home Screen". */}
            <link rel="apple-touch-icon" href={config?.appIcons?.["180"] ?? logoUrl} />
            <link rel="shortcut icon" href={logoUrl} />
          </Helmet>
          {/* HTPTV-714: sets a distinct, per-route document.title for GA4. */}
          <RouteTitle />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            limit={2}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            icon={false}
            pauseOnFocusLoss={false}
            draggable
            pauseOnHover={false}
            theme="light"
          />
          {/* HTPTV-738: app-global so the past_due banner works on every shell. */}
          <SubscriptionStatusProvider>
            {/*
              One boundary for every lazy v2 route, deliberately outside
              Routes.

              Each route used to carry its own, and React shows a fallback the
              moment a boundary is newly mounted, transition or not. So every
              first visit to a page unmounted the page you were on and rendered
              nothing while the next chunk downloaded: a blank flash, once per
              page, then never again because the chunk was cached.

              This boundary is already mounted when a navigation starts, so
              with route changes being transitions (see the Router's future
              flag) React holds the current page on screen until the next one
              is ready, so the fallback is not seen on any navigation inside
              the app.

              It IS seen on a direct landing: a pasted link, a bookmark, a
              refresh. There is no previous page to hold, the boundary mounts
              fresh, and React shows the fallback while the chunk downloads.
              That case used to be null, which is a blank white screen for as
              long as that takes, and on /lens/universe it was reported as
              several seconds. A spinner instead, so a cold load reads as
              loading rather than as broken.

              v1 routes are not lazy and never suspend, so this changes nothing
              for them.
            */}
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* HTPTV2-2: v1 Login/Register are fully superseded by the v2
                sign-in card. PageRoutes.Login is now just an alias for
                PageRoutes.SignIn (both "/sign-in"), so existing
                links/redirects (PrivateRoute -> PageRoutes.Login) still work
                without a second, duplicate route registration here.
                PageRoutes.Register ("/create-account") stays a distinct path
                so "Register now" links still carry location.state.from
                through to the v2 form. */}
              <Route
                path={PageRoutes.Register}
                element={
                  <PublicRoute useV2Layout>
                    <V2SignInRoute />
                  </PublicRoute>
                }
              />
              <Route
                path={PageRoutes.SignIn}
                element={
                  <PublicRoute useV2Layout>
                    <V2SignInRoute />
                  </PublicRoute>
                }
              />
              {/* HTPTV-636: redeem reachable whether logged in or not */}
              <Route
                path={PageRoutes.RedeemGiftPage}
                element={<RedeemGift />}
              />
              {/* The phone's second-screen route, same reasoning as
                RedeemGiftPage above: it must be reachable whether the phone
                is logged in or not, since the whole point is that it never
                has a tenant session of its own. */}
              <Route
                path={PageRoutes.SecondScreen}
                element={<SecondScreen />}
              />
              <Route
                path={PageRoutes.PasswordReset}
                element={
                  <PublicRoute useV2Layout>
                    <V2ResetPasswordRoute />
                  </PublicRoute>
                }
              />
              <Route
                path={PageRoutes.VerifyEmail}
                element={
                  <PublicRoute>
                    <VerifyEmail />
                  </PublicRoute>
                }
              />
              <Route
                path={PageRoutes.HomePage}
                element={
                  <BlockingWrapper>
                    <PublicRoute useV2Layout>
                      <V2HomeRoute />
                    </PublicRoute>
                  </BlockingWrapper>
                }
              />
              {config?.planpage && (
                <Route
                  path={PageRoutes.Packages}
                  element={
                    <PublicRoute useV2Layout>
                      <V2PlansRoute />
                    </PublicRoute>
                  }
                />
              )}
              <Route
                path={PageRoutes.TermAndConditions}
                element={
                  <PublicRoute hideFooter>
                    <TermAndCondition />
                  </PublicRoute>
                }
              />
              <Route
                path={PageRoutes.PrivacyPolicy}
                element={
                  <PublicRoute hideFooter>
                    <PrivacyPolicy />
                  </PublicRoute>
                }
              />
              <Route
                path={PageRoutes.ContactUS}
                element={
                  <PublicRoute>
                    <SupportPage />
                  </PublicRoute>
                }
              />
              <Route
                path={PageRoutes.Setting}
                element={
                  <PrivateRoutes useV2Layout>
                    <Profile />
                  </PrivateRoutes>
                }
              />
              {/* Account Settings is superseded by the Profile page. */}
              <Route
                path={PageRoutes.AccountSettings}
                element={<Navigate to={PageRoutes.Setting} replace />}
              />
              <Route
                path={PageRoutes.Admin}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.SliderSetting}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                        UserRoles.CUSTOMER_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.EditAds}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <CreateAds />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.Users}
                element={
                  <PrivateRoutes>
                    <AdminRoutes allowedUser={[UserRoles.HTP_ADMIN]}>
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.PlayerAlerts}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.Media}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.MediaCreate}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <AddMedia />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.Ads}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.Polls}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.Reports}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              {process.env.REACT_APP_INSIDER == "true" && (
                <Route
                  path={PageRoutes.Whizzard}
                  element={
                    <PrivateRoutes>
                      <AdminRoutes
                        allowedUser={[
                          UserRoles.HTP_ADMIN,
                          UserRoles.LEAGUE_ADMIN,
                        ]}
                      >
                        <Admin />
                      </AdminRoutes>
                    </PrivateRoutes>
                  }
                />
              )}

              <Route
                path={PageRoutes.UserView}
                element={
                  <PrivateRoutes>
                    <AdminRoutes allowedUser={[UserRoles.HTP_ADMIN]}>
                      <UserView />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              {process.env.REACT_APP_INSIDER == "true" && (
                <Route
                  path={PageRoutes.EditWhizzard}
                  element={
                    <PrivateRoutes>
                      <AdminRoutes
                        allowedUser={[
                          UserRoles.HTP_ADMIN,
                          UserRoles.LEAGUE_ADMIN,
                        ]}
                      >
                        <EditWhizzard />
                      </AdminRoutes>
                    </PrivateRoutes>
                  }
                />
              )}

              <Route
                path={PageRoutes.EditAnnouncement}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <TinyEditor />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />

              <Route
                path={PageRoutes.EditSliderSetting}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                        UserRoles.CUSTOMER_ADMIN,
                      ]}
                    >
                      <CreateSliderForm />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.GameSummary}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <Admin />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.EditPlayerAlerts}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <CreatePlayerError />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              {/* NA 24x7 tenants only, like the /na-24x7 guide it feeds. */}
              {isLiveChannelTenant(syncTenantName) && (
                <Route
                  path={PageRoutes.ChannelScheduler}
                  element={
                    <PrivateRoutes>
                      <AdminRoutes
                        allowedUser={[
                          UserRoles.HTP_ADMIN,
                          UserRoles.LEAGUE_ADMIN,
                        ]}
                      >
                        <Admin />
                      </AdminRoutes>
                    </PrivateRoutes>
                  }
                />
              )}
              {hasGeoLocation && (
                <Route
                  path={PageRoutes.Games}
                  element={
                    <PrivateRoutes>
                      <AdminRoutes
                        allowedUser={[
                          UserRoles.HTP_ADMIN,
                          UserRoles.LEAGUE_ADMIN,
                        ]}
                      >
                        <Admin />
                      </AdminRoutes>
                    </PrivateRoutes>
                  }
                />
              )}
              {hasGeoLocation && (
                <Route
                  path={PageRoutes.GameView}
                  element={
                    <PrivateRoutes>
                      <AdminRoutes
                        allowedUser={[
                          UserRoles.HTP_ADMIN,
                          UserRoles.LEAGUE_ADMIN,
                        ]}
                      >
                        <GameView />
                      </AdminRoutes>
                    </PrivateRoutes>
                  }
                />
              )}
              {hasGeoLocation && (
                <Route
                  path={PageRoutes.Rules}
                  element={
                    <PrivateRoutes>
                      <AdminRoutes
                        allowedUser={[
                          UserRoles.HTP_ADMIN,
                          UserRoles.LEAGUE_ADMIN,
                        ]}
                      >
                        <Admin />
                      </AdminRoutes>
                    </PrivateRoutes>
                  }
                />
              )}
              <Route
                path={PageRoutes.EditPolls}
                element={
                  <PrivateRoutes>
                    <AdminRoutes
                      allowedUser={[
                        UserRoles.HTP_ADMIN,
                        UserRoles.LEAGUE_ADMIN,
                      ]}
                    >
                      <CreatePoll />
                    </AdminRoutes>
                  </PrivateRoutes>
                }
              />
              {hasGeoLocation && (
                <Route
                  path={PageRoutes.RulesView}
                  element={
                    <PrivateRoutes>
                      <AdminRoutes
                        allowedUser={[
                          UserRoles.HTP_ADMIN,
                          UserRoles.LEAGUE_ADMIN,
                        ]}
                      >
                        <RuleView />
                      </AdminRoutes>
                    </PrivateRoutes>
                  }
                />
              )}
              <Route
                path={PageRoutes.Alert}
                element={
                  <PrivateRoutes useV2Layout>
                    <V2NotificationsRoute />
                  </PrivateRoutes>
                }
              />

              <Route
                path={PageRoutes.Any}
                element={
                  <PublicRoute>
                    <NoPageFound />
                  </PublicRoute>
                }
              />
              <Route
                path={PageRoutes.EditProfile}
                element={
                  <PrivateRoutes>
                    <EditProfile />
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.UpcomingEvent}
                element={
                  <BlockingWrapper>
                    <V2UpcomingEventRoute />
                  </BlockingWrapper>
                }
              />
              <Route
                path={PageRoutes.OnDemand}
                element={
                  <BlockingWrapper>
                    <V2OnDemandRoute />
                  </BlockingWrapper>
                }
              />
              {isLiveChannelTenant(syncTenantName) && (
                <Route
                  path={PageRoutes.LiveTV}
                  element={
                    // Logged-in only, matching the nav tab (requiresAuth):
                    // hiding the tab alone would leave the URL reachable.
                    <PrivateRoutes useV2Layout>
                      <BlockingWrapper>
                        <V2LiveTVRoute />
                      </BlockingWrapper>
                    </PrivateRoutes>
                  }
                />
              )}
              {process.env.REACT_APP_INSIDER == "true" && (
                <Route
                  path={PageRoutes.MyNAHLTV}
                  element={
                    <PrivateRoutes>
                      <MyNahlTv />
                    </PrivateRoutes>
                  }
                />
              )}
              {process.env.REACT_APP_INSIDER == "true" && (
                <Route
                  path={PageRoutes.PersonalChannel}
                  element={
                    <PrivateRoutes>
                      <PersonalChannelSubscribe />
                    </PrivateRoutes>
                  }
                />
              )}
              {process.env.REACT_APP_INSIDER == "true" && (
                <Route
                  path={PageRoutes.AcceptChannelShare}
                  element={
                    <PrivateRoutes>
                      <ChannelShareAction action="accept" />
                    </PrivateRoutes>
                  }
                />
              )}
              {process.env.REACT_APP_INSIDER == "true" && (
                <Route
                  path={PageRoutes.DeclineChannelShare}
                  element={
                    <PrivateRoutes>
                      <ChannelShareAction action="decline" />
                    </PrivateRoutes>
                  }
                />
              )}
              {/*
                The four channel-era paths, kept alive as redirects. Share
                invitations already in people's inboxes point at
                /channel-share/accept and stay valid for seven days, and a
                bookmarked /personal-channel link never expires at all. Gated
                on the same flag as the routes above, since there is nothing
                to redirect to when the feature is off.
              */}
              {process.env.REACT_APP_INSIDER == "true" && (
                <>
                  <Route
                    path={LegacyPageRoutes.MyNAHLTV}
                    element={<LegacyRedirect to={PageRoutes.MyNAHLTV} />}
                  />
                  <Route
                    path={LegacyPageRoutes.PersonalChannel}
                    element={<LegacyRedirect to={PageRoutes.PersonalChannel} />}
                  />
                  <Route
                    path={LegacyPageRoutes.AcceptChannelShare}
                    element={
                      <LegacyRedirect to={PageRoutes.AcceptChannelShare} />
                    }
                  />
                  <Route
                    path={LegacyPageRoutes.DeclineChannelShare}
                    element={
                      <LegacyRedirect to={PageRoutes.DeclineChannelShare} />
                    }
                  />
                </>
              )}
              {process.env.REACT_APP_INSIDER == "true" && (
                <Route
                  path={PageRoutes.HighLightPlayer}
                  element={
                    <PrivateRoutes>
                      <Highlights />
                    </PrivateRoutes>
                  }
                />
              )}
              <Route
                path={PageRoutes.paymentPage}
                element={
                  <PaymentRouteElement />
                }
              />
              <Route
                path="/plans/success"
                element={
                  <PrivateRoutes useV2Layout>
                    <V2PurchaseSuccessRoute />
                  </PrivateRoutes>
                }
              />
              {["nahl", "dev", "ssm", "usah"].includes(config.name) && (
                <Route
                  path={PageRoutes.ShortContent}
                  element={
                    // Not wrapped in PrivateRoutes: the v2 page gates its
                    // own content via AccessGateOverlay, which needs to render
                    // for logged-out visitors too (login CTA vs. upgrade CTA
                    // depend on knowing that state).
                    <V2HighlightsRoute />
                  }
                />
              )}
              {["nahl", "dev", "ssm", "usah"].includes(config.name) && (
                <Route
                  path={PageRoutes.FilterShortContent}
                  element={
                    // Same reasoning as ShortContent/ShortView: AccessGateOverlay
                    // needs to see logged-out visitors, not have them redirected
                    // to Login before it ever mounts.
                    <V2DrillDownRoute />
                  }
                />
              )}
              {["nahl", "dev", "ssm", "usah"].includes(config.name) && (
                <Route
                  path={PageRoutes.ShortView}
                  element={
                    // Same reasoning as ShortContent above: AccessGateOverlay
                    // needs to see logged-out visitors, not have them
                    // redirected to Login before it ever mounts.
                    <V2ShortViewRoute />
                  }
                />
              )}
              {["nahl", "dev"].includes(config.name) && is_team === "true" && (
                <Route
                  path={PageRoutes.Team}
                  element={
                    <PrivateRoutes>
                      <Team />
                    </PrivateRoutes>
                  }
                />
              )}
              {config?.planpage && (
                <Route
                  path={PageRoutes.GiftPage}
                  element={
                    <PrivateRoutes useV2Layout>
                      <GiftPage />
                    </PrivateRoutes>
                  }
                />
              )}
              <Route
                path={PageRoutes.CancelSaveFlow}
                element={
                  <PrivateRoutes useV2Layout>
                    <V2CancelFlowRoute />
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.Watch}
                element={
                  <BlockingWrapper>
                    <PrivateRoutes>
                      <WatchGame />
                    </PrivateRoutes>
                  </BlockingWrapper>
                }
              />
              {V2PreviewRoute && (
                <Route
                  path="/v2/preview"
                  element={
                    <V2PreviewRoute />
                  }
                />
              )}
              {V2EventsRoute && (
                <Route
                  path="/v2/events"
                  element={
                    <V2EventsRoute />
                  }
                />
              )}
              {V2UpcomingEventRoute && (
                <Route
                  path="/v2/upcoming-event"
                  element={
                    <V2UpcomingEventRoute />
                  }
                />
              )}
              {V2HighlightsRoute && (
                <Route
                  path="/v2/highlights"
                  element={
                    <V2HighlightsRoute />
                  }
                />
              )}
              {V2ShortViewRoute && (
                <Route
                  path="/v2/short-view/:id"
                  element={
                    <V2ShortViewRoute />
                  }
                />
              )}
              {V2OnDemandRoute && (
                <Route
                  path="/v2/vod"
                  element={
                    <V2OnDemandRoute />
                  }
                />
              )}
              {/*
                ONE FAVOURITES PAGE, AT /lens/favorites.

                Both routes rendered the same FavoritesManagerContent and
                differed only in which sidebar wrapped it, so a fan could reach
                two URLs showing the same thing with a different rail beside
                it, and nothing said which was the real one.

                Kept as a redirect rather than deleted: it is a route people
                have bookmarked, and the two in-app buttons that used to point
                here now point at the page itself, so nothing inside the app
                takes this hop.
              */}
              <Route
                path={PageRoutes.FavoritesManager}
                element={
                  <PrivateRoutes useV2Layout>
                    <Navigate to={PageRoutes.LensFavorites} replace />
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.Lens}
                element={
                  <PrivateRoutes useV2Layout>
                    <Navigate to={PageRoutes.LensUniverse} replace />
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.LensFavorites}
                element={
                  <PrivateRoutes useV2Layout>
                    <V2LensFavoritesRoute />
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.LensUniverse}
                element={
                  <PrivateRoutes useV2Layout>
                    <V2LensUniverseRoute />
                  </PrivateRoutes>
                }
              />
              <Route
                path={PageRoutes.LensStudio}
                element={
                  <PrivateRoutes useV2Layout>
                    <V2LensStudioRoute />
                  </PrivateRoutes>
                }
              />
              {config?.planpage && (
                <Route
                  path={PageRoutes.AccountPage}
                  element={
                    <PrivateRoutes useV2Layout>
                      <V2AccountRoute />
                    </PrivateRoutes>
                  }
                />
              )}
              {[
                { path: PageRoutes.WatchHistory, title: "Watch History" },
                { path: PageRoutes.MyWatchParties, title: "My Watch Parties" },
                {
                  path: PageRoutes.PurchaseHistoryPage,
                  title: "Purchase History",
                },
                { path: PageRoutes.Billing, title: "Billing & Payments" },
                { path: PageRoutes.Devices, title: "Devices" },
                {
                  path: PageRoutes.NotificationSettings,
                  title: "Notification Settings",
                },
              ].map((page) => (
                <Route
                  key={page.path}
                  path={page.path}
                  element={
                    <PrivateRoutes useV2Layout>
                      <AccountComingSoon title={page.title} />
                    </PrivateRoutes>
                  }
                />
              ))}
            </Routes>
            </Suspense>
          </SubscriptionStatusProvider>
        </LoadingContextProvider>
      </ThemeProviderReact>
    </Router>
  );
}

export default App;
